import React, { useState } from 'react';
import { Calendar, Clock, Star, Plus, AlertTriangle } from 'lucide-react';
import { useUser } from '../hooks/useUser';
import { formatExerciseTitle } from '../utils/formatExerciseTitle';
import { CreateQuestRequest, MAX_ACTIVE_QUESTS, Quest } from '@unity-learning/shared';
import { createQuest, getActiveQuests, type QuestWithParticipation } from '../services/api';
import toast from 'react-hot-toast';

interface AdminQuestButtonProps {
  exerciseId: string;
  exerciseTitle: string;
  onQuestCreated?: (quest: Quest) => void;
}

const AdminQuestButton: React.FC<AdminQuestButtonProps> = ({
  exerciseId,
  exerciseTitle,
  onQuestCreated
}) => {
  const { user } = useUser();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [scheduleType, setScheduleType] = useState<'now' | 'later'>('now');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  // Quêtes actives à l'ouverture de la fenêtre : pour nommer celle qui se terminerait, ou signaler un doublon
  const [activeQuests, setActiveQuests] = useState<QuestWithParticipation[] | null>(null);

  // Only show for admin users
  if (!user || user.role !== 'admin') {
    return null;
  }

  const handleCreateQuest = async () => {
    setIsLoading(true);
    
    try {
      let scheduledAt: Date | undefined;
      
      if (scheduleType === 'later') {
        if (!scheduledDate || !scheduledTime) {
          toast.error('Veuillez sélectionner une date et une heure');
          setIsLoading(false);
          return;
        }
        
        scheduledAt = new Date(`${scheduledDate}T${scheduledTime}`);
        
        // Validate that scheduled time is in the future
        if (scheduledAt <= new Date()) {
          toast.error('La date/heure programmée doit être dans le futur');
          setIsLoading(false);
          return;
        }
      }

      const requestBody: CreateQuestRequest = {
        exerciseId,
        title: exerciseTitle,
        schedule: scheduleType,
        scheduledAt: scheduledAt?.toISOString()
      };

      const { quest, message } = await createQuest(requestBody);

      toast.success(message || 'Quête créée avec succès!');
      setIsModalOpen(false);
      
      if (onQuestCreated) {
        onQuestCreated(quest);
      }

    } catch (error) {
      console.error('Error creating quest:', error);
      toast.error((error as Error).message || 'Erreur lors de la création de la quête');
    } finally {
      setIsLoading(false);
    }
  };

  const resetModal = () => {
    setScheduleType('now');
    setScheduledDate('');
    setScheduledTime('');
  };

  const handleOpenModal = () => {
    resetModal();
    setActiveQuests(null);
    setIsModalOpen(true);
    getActiveQuests()
      .then(setActiveQuests)
      .catch(() => { /* sans la liste, l'avertissement général reste affiché */ });
  };

  const alreadyActive = activeQuests?.some((quest) => quest.exerciseId === exerciseId) ?? false;
  const questToEnd = activeQuests && activeQuests.length >= MAX_ACTIVE_QUESTS
    ? activeQuests[activeQuests.length - 1]
    : null;

  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const getDefaultTime = () => {
    const now = new Date();
    now.setHours(now.getHours() + 1);
    return now.toTimeString().slice(0, 5);
  };

  return (
    <>
      {/* Admin Quest Button */}
      <div className="mb-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Star className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            <span className="text-sm font-medium text-amber-800 dark:text-amber-300">
              Mode Administrateur
            </span>
          </div>
          <button
            onClick={handleOpenModal}
            className="inline-flex items-center px-3 py-1.5 text-sm bg-amber-600 text-white rounded-md hover:bg-amber-700 transition-colors ml-6"
          >
            <Plus className="w-4 h-4 mr-1" />
            Ajouter comme Quête
          </button>
        </div>
      </div>

      {/* Quest Creation Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Créer une Quête
              </h3>

              <div className="space-y-4">
                {/* Exercise Info */}
                <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Exercice:</p>
                  <p className="font-medium text-gray-900 dark:text-white">{formatExerciseTitle(exerciseTitle)}</p>
                </div>

                {/* Schedule Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Quand activer cette quête ?
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="scheduleType"
                        value="now"
                        checked={scheduleType === 'now'}
                        onChange={(e) => setScheduleType(e.target.value as 'now' | 'later')}
                        className="mr-2"
                      />
                      <Clock className="w-4 h-4 mr-2 text-green-600 dark:text-green-400" />
                      <span className="text-sm text-gray-900 dark:text-gray-200">Maintenant (s'ajoute aux quêtes actives)</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="scheduleType"
                        value="later"
                        checked={scheduleType === 'later'}
                        onChange={(e) => setScheduleType(e.target.value as 'now' | 'later')}
                        className="mr-2"
                      />
                      <Calendar className="w-4 h-4 mr-2 text-blue-600 dark:text-blue-400" />
                      <span className="text-sm text-gray-900 dark:text-gray-200">Programmer pour plus tard</span>
                    </label>
                  </div>
                </div>

                {/* Schedule Later Options */}
                {scheduleType === 'later' && (
                  <div className="space-y-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Date
                      </label>
                      <input
                        type="date"
                        value={scheduledDate}
                        onChange={(e) => setScheduledDate(e.target.value)}
                        min={getTodayDate()}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Heure
                      </label>
                      <input
                        type="time"
                        value={scheduledTime}
                        onChange={(e) => setScheduledTime(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div className="flex items-start space-x-2 text-sm text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 p-2 rounded">
                      <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <p>
                        Elle s'ajoutera aux quêtes actives à cette date ; les autres quêtes programmées restent programmées.
                        S'il y a déjà {MAX_ACTIVE_QUESTS} quêtes actives à ce moment-là, la plus ancienne se terminera.
                      </p>
                    </div>
                  </div>
                )}

                {/* Warning for Now option */}
                {scheduleType === 'now' && (
                  <div className="flex items-start space-x-2 text-sm text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 p-2 rounded">
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <p>
                      Elle s'ajoute aux quêtes actives de tous les étudiants. S'il y en a déjà {MAX_ACTIVE_QUESTS}, la plus
                      ancienne se termine (son exercice reste jouable, sans compter pour la quête).
                      {questToEnd && !alreadyActive && (
                        <span className="block mt-1 font-medium">
                          Il y en a déjà {activeQuests!.length} : « {formatExerciseTitle(questToEnd.title)} » se terminera.
                        </span>
                      )}
                      {alreadyActive && (
                        <span className="block mt-1 font-medium">
                          Cet exercice est déjà une quête active : la publication sera refusée.
                        </span>
                      )}
                    </p>
                  </div>
                )}
              </div>

              {/* Modal Actions */}
              <div className="flex items-center justify-end space-x-3 mt-6">
                <button
                  onClick={() => setIsModalOpen(false)}
                  disabled={isLoading}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleCreateQuest}
                  disabled={isLoading || (scheduleType === 'later' && (!scheduledDate || !scheduledTime))}
                  className="inline-flex items-center px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Création...
                    </>
                  ) : (
                    <>
                      <Star className="w-4 h-4 mr-2" />
                      {scheduleType === 'now' ? 'Créer maintenant' : 'Programmer'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminQuestButton;
